import Joi from 'joi-browser';
import * as _ from 'lodash';
import localize, { localizeMessage } from './validateMessage';
import regex from '../utilities/validateRegex';

export const validationByJoi_New = (schema, data) => {
  const result = Joi.validate(data, schema, {
    abortEarly: false,
    language: localize,
    allowUnknown: true,
  });

  if (result.error) {
    const messages = {};
    result.error.details.forEach(detail => {
      if (_.isUndefined(detail.context)) {
        setPath(messages, detail.path, '値が間違っています。');
        return true;
      }
      if (detail.type && detail.type === 'customMessage') {
        setPath(messages, detail.path, detail.context.errorMessage);
        return;
      }
      if (!_.isUndefined(detail.context.name)) {
        if (_.isUndefined(regex.message[detail.context.name])) {
          throw new Error('エラーに対応するメッセージがみつかりません');
        }
        setPath(messages, detail.path, regex.message[detail.context.name]);
      }
      if (!_.isUndefined(detail.context.label)) {
        if (_.isUndefined(regex.message[detail.context.label])) {
          throw new Error('エラーに対応するメッセージがみつかりません');
        }
        setPath(messages, detail.path, regex.message[detail.context.label]);
      }
    });

    return { status: false, messages }; // エラーメッセージを返す。
  }

  return { status: true, messages: {} }; // エラー無し
};

/**
 * @description joiを使ってバリエーション
 * @param {Object} schema joiの型定義オブジェクト
 * @param {Any} data バリエーションするデータ
 */
export const validationByJoi = (schema, data) => {
  const result = Joi.validate(data, schema, {
    abortEarly: false,
    language: localize,
    allowUnknown: true,
  });

  if (result.error) {
    const messages = {};
    result.error.details.forEach(detail => {
      if (detail.type && detail.type === 'customMessage') {
        setPath(messages, detail.path, detail.context.errorMessage);
        return;
      }
      if (detail.context !== undefined && detail.context.name !== undefined) {
        setPath(
          messages,
          detail.path,
          regex.message[detail.context.name] || '値が間違っています。'
        );
      } else {
        const mt = detail.message.match(/\[(.*)\]/i);
        if (mt) {
          if (
            mt[1] === 'string_base' ||
            mt[1] === 'string_min' ||
            mt[1] === 'string_max' ||
            mt[1] === 'string_length'
          ) {
            const mt2 = detail.message.match(/\[(.*)\](.*)/i);
            setPath(
              messages,
              detail.path,
              `${mt2[2]}${localizeMessage[mt[1]]}` || '値が間違っています。'
            );
          } else {
            setPath(
              messages,
              detail.path,
              localizeMessage[mt[1]] || '値が間違っています。'
            );
          }
        } else {
          setPath(messages, detail.path, '値が間違っています。');
        }
      }
    });

    return { status: false, messages }; // エラーメッセージを返す。
  }

  return { status: true, messages: {} }; // エラー無し
};

export const validationRegexByJoi = (schema, data, allowUnknown = false) => {
  const result = Joi.validate(data, schema, {
    abortEarly: false,
    language: localize,
    allowUnknown,
  });

  if (result.error) {
    const messages = {};
    result.error.details.forEach(detail => {
      const mt = detail.message.match(/\[(.*)\]/i);
      const _regex = detail.type.match(/regex/i);
      if (_regex) {
        let _pattern = '';
        _.each(regex.pattern, (value, key) => {
          if (`${value}` == `${detail.context.pattern}`) {
            _pattern = key;
          }
        });
        setPath(messages, detail.path, regex.message[_pattern]);
      } else if (detail.type === 'validation.custom') {
        setPath(messages, detail.path, detail.message);
      } else {
        if (mt) {
          if (
            mt[1] === 'string_base' ||
            mt[1] === 'string_min' ||
            mt[1] === 'string_max' ||
            mt[1] === 'string_length'
          ) {
            const mt2 = detail.message.match(/\[(.*)\](.*)/i);
            setPath(
              messages,
              detail.path,
              `${mt2[2]}${localizeMessage[mt[1]]}` || '値が間違っています。'
            );
          } else {
            setPath(
              messages,
              detail.path,
              localizeMessage[mt[1]] || '値が間違っています。'
            );
          }
        } else {
          setPath(messages, detail.path, '値が間違っています。');
        }
      }
    });

    return { status: false, messages }; // エラーメッセージを返す。
  }

  return { status: true, messages: {} }; // エラー無し
};

function setPath(obj, path, value) {
  let key = path;

  if (_.isArray(path)) {
    key = path.shift();

    if (path.length > 0) {
      if (!obj[key]) {
        obj[key] = {};
      }
      return setPath(obj[key], path, value);
    }
  }

  // keyがindefinedになる対策
  if (!_.isUndefined(key)) {
    obj[key] = value;
  }
}

export const validatePasswordPolicy = (errors, privatePasswordPolicy) => {
  const [error] = errors;
  const password = error.context.value;
  if (password === undefined || password === '') {
    return {
      type: 'validation.custom',
      message: regex.message.must,
    };
  }

  const passwordLength = password.length;
  const passwordPolicy = privatePasswordPolicy.password_policy;
  const policyDescription = privatePasswordPolicy.policy_description;
  const passwordMinLenghth = passwordPolicy.min_length;
  const passwordMaxLenghth = passwordPolicy.max_length;
  const requiredCharacters = passwordPolicy.required_characters || [];
  const responseWithError = {
    type: 'validation.custom',
    message: policyDescription,
  };

  if (
    (passwordMinLenghth && passwordLength < passwordMinLenghth) ||
    (passwordMaxLenghth && passwordLength > passwordMaxLenghth)
  ) {
    return responseWithError;
  }

  let isValid = true;
  requiredCharacters.forEach(pt => {
    if (_.intersection(pt.split(''), password.split('')).length === 0) {
      isValid = false;
      return;
    }
  });
  if (!isValid) {
    return responseWithError;
  }

  const optionsCharacters = passwordPolicy.optional_characters || [];
  const availableCharacters = _.concat(requiredCharacters, optionsCharacters);
  isValid = password.split('').every(char => {
    return availableCharacters.join().includes(char);
  });
  if (!isValid) {
    return responseWithError;
  }

  return [];
};



// WEBPACK FOOTER //
// ./src/utilities/commonValidation.js